import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  LayoutDashboard,
  Brain,
  BookOpen,
  Terminal,
  FileEdit,
  Users,
  FileText,
  AlertTriangle,
  CheckCircle,
  Settings,
  ArrowRight,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, subtext, color, onClick }) => (
  <div 
    className={`elite-card p-5 cursor-pointer hover:shadow-md transition-all ${onClick ? 'hover:scale-[1.02]' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-500 mb-1">{label}</p>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
        {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
      </div>
      <div className={`p-3 rounded-xl bg-opacity-10 ${color.replace('text-', 'bg-')}`}>
        <Icon className={color} size={24} />
      </div>
    </div>
  </div>
);

const QuickActionCard = ({ icon: Icon, title, description, to, color }) => {
  const navigate = useNavigate();
  return (
    <div 
      className="elite-card p-5 cursor-pointer hover:shadow-md transition-all group"
      onClick={() => navigate(to)}
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="text-white" size={20} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-stone-900 group-hover:text-amber-600 transition-colors">
            {title}
          </h3>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <ArrowRight className="text-slate-400 group-hover:text-amber-600 transition-colors" size={20} />
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Use admin-ops/overview for secure architecture
      const response = await axios.get(`${API}/admin-ops/overview`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      // Map admin-ops overview response to expected stats format
      const data = response.data;
      setStats({
        parser_training: {
          rules_count: data.counts?.parser_candidates || 0,
          feedback_count: data.intake_counts?.total || 0,
          pending_reviews: data.counts?.pending_reviews || 0
        },
        knowledge_base: {
          total_entries: data.downstream_counts?.knowledge_base?.total || data.counts?.knowledge_entries || 0,
          active_entries: data.downstream_counts?.knowledge_base?.total || data.counts?.knowledge_entries || 0
        },
        prompts: {
          total_prompts: data.counts?.prompt_versions || 0,
          active_prompts: data.counts?.prompt_versions || 0
        },
        letter_training: {
          total_materials: data.downstream_counts?.letter_training?.total || data.counts?.letter_materials || 0
        },
        users: {
          total_users: 0, // Fetched separately if needed
          admin_users: 0,
          specialist_users: 0
        },
        reports: {
          total_reports: 0 // Fetched separately if needed
        }
      });
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!['admin', 'specialist', 'platform_owner', 'platform_admin', 'owner'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-stone-900 mb-2">Admin Access Required</h2>
        <p className="text-slate-500">This page is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-3">
            <LayoutDashboard className="text-amber-500" />
            Admin Panel
          </h1>
          <p className="text-sm text-slate-500">Internal AI improvement and training tools</p>
        </div>
        <Button variant="outline" onClick={fetchStats}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Important Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-amber-800">Admin-Only Access</h3>
            <p className="text-sm text-amber-700">
              These tools are for internal use only. Normal users cannot access or see these pages.
              All training materials and configurations stay private.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={AlertTriangle}
            label="Pending Reviews"
            value={stats?.parser_training?.pending_reviews || 0}
            subtext="Items awaiting classification"
            color="text-amber-600"
            onClick={() => navigate('/admin/parser-training')}
          />
          <StatCard
            icon={BookOpen}
            label="Knowledge Entries"
            value={stats?.knowledge_base?.total_entries || 0}
            subtext={`${stats?.knowledge_base?.active_entries || 0} active`}
            color="text-emerald-600"
            onClick={() => navigate('/admin/knowledge-base')}
          />
          <StatCard
            icon={Terminal}
            label="Prompt Versions"
            value={stats?.prompts?.total_prompts || 0}
            subtext={`${stats?.prompts?.active_prompts || 0} active`}
            color="text-amber-600"
            onClick={() => navigate('/admin/prompts')}
          />
          <StatCard
            icon={FileEdit}
            label="Letter Materials"
            value={stats?.letter_training?.total_materials || 0}
            subtext="Training examples"
            color="text-emerald-600"
            onClick={() => navigate('/admin/letter-training')}
          />
        </div>
      )}

      {/* Additional Stats */}
      {!loading && stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={Users}
            label="Total Users"
            value={stats?.users?.total_users || 0}
            subtext={`${stats?.users?.admin_users || 0} admins, ${stats?.users?.specialist_users || 0} specialists`}
            color="text-slate-600"
          />
          <StatCard
            icon={FileText}
            label="Credit Reports"
            value={stats?.reports?.total_reports || 0}
            subtext="Processed reports"
            color="text-amber-600"
          />
          <StatCard
            icon={CheckCircle}
            label="Parser Rules"
            value={stats?.parser_training?.rules_count || 0}
            subtext={`${stats?.parser_training?.feedback_count || 0} training items`}
            color="text-teal-600"
            onClick={() => navigate('/admin/parser-training')}
          />
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-stone-900 mb-4">Admin Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuickActionCard
            icon={Brain}
            title="Parser Training"
            description="Review extracted items, manage classification rules"
            to="/admin/parser-training"
            color="bg-amber-500"
          />
          <QuickActionCard
            icon={BookOpen}
            title="AI Knowledge Base"
            description="Manage transcripts, SOPs, and training materials"
            to="/admin/knowledge-base"
            color="bg-emerald-500"
          />
          <QuickActionCard
            icon={Terminal}
            title="Prompt Management"
            description="Version and manage AI prompts"
            to="/admin/prompts"
            color="bg-emerald-500"
          />
          <QuickActionCard
            icon={FileEdit}
            title="Letter Training"
            description="Improve dispute letter generation"
            to="/admin/letter-training"
            color="bg-amber-500"
          />
        </div>
      </div>

      {/* System Status */}
      <div className="elite-card p-6">
        <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
          <Settings size={20} className="text-slate-500" />
          System Status
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-slate-600">Parser Engine</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-slate-600">AI Analysis</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-slate-600">Knowledge Base</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-slate-600">Letter Generator</span>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-slate-50 rounded-xl p-4 text-center">
        <p className="text-xs text-slate-500">
          All admin actions are logged. Training materials, prompts, and configurations are not exposed to end users.
          Normal clients only see the improved AI outputs, not the training system.
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
